/**
 * Transfer windows (1.1.0): named weekly hours, in a time zone, when storage migrations and tiering rules may move
 * files ("Mon–Fri 23:00–05:00, Sat–Sun all day"). Pure and deterministic (Intl only, through date-schedule.ts), so the
 * API's scheduler, the wizard's estimate and the tests compute the same times.
 *
 * Rules:
 *  - a period belongs to the local day it starts on: Mon 23:00–05:00 runs from Monday 23:00 to Tuesday 05:00;
 *  - `from > to` crosses midnight, `to` may be `24:00` (the end of the day), and `from == to` means 24 hours;
 *  - each bound is found with zonedTimeToUtc on its own local date, so a period keeps its wall-clock hours across a
 *    change of summer time: a bound in the spring gap moves forward by the gap (the period gets shorter, never
 *    skipped), a bound in the autumn repeat takes the first occurrence (the period gets longer);
 *  - intervals that touch or overlap are merged into one;
 *  - a null window is always open.
 *
 * Instants are epoch milliseconds throughout.
 */
import { z } from 'zod';
import { isTimeZone, wallTimeOf, zonedTimeToUtc } from './date-schedule.ts';

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export const TransferWindowPeriodSchema = z.object({
  /** The local days a period starts on. */
  days: z.array(z.enum(WEEKDAYS)).min(1).max(7),
  /** HH:MM, 00:00–23:59. */
  from: z.string().regex(HHMM, 'HH:MM'),
  /** HH:MM, or 24:00 for the end of the day. Earlier than `from`: the next day; equal: 24 hours. */
  to: z.string().refine((v) => v === '24:00' || HHMM.test(v), 'HH:MM or 24:00'),
});
export type TransferWindowPeriod = z.infer<typeof TransferWindowPeriodSchema>;

export const TransferWindowSchema = z.object({
  /** IANA time zone, e.g. Europe/Helsinki. */
  timezone: z.string().min(1).max(64).refine(isTimeZone, 'Unknown time zone'),
  periods: z.array(TransferWindowPeriodSchema).min(1).max(50),
});
export type TransferWindow = z.infer<typeof TransferWindowSchema>;

export interface OpenInterval {
  start: number;
  end: number;
}

const DAY_MS = 86_400_000;
/** How far nextOpening and nextClosing look ahead. */
export const WINDOW_LOOKAHEAD_MS = 8 * DAY_MS;
/** How far finishAfter looks ahead before it gives up (null). */
const FINISH_HORIZON_MS = 400 * DAY_MS;
/** getUTCDay() (0 = Sunday) to a weekday. */
const BY_UTC_DAY: Weekday[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function hm(v: string): { h: number; mi: number } {
  return { h: Number(v.slice(0, 2)), mi: Number(v.slice(3, 5)) };
}

/** The UTC instant of a wall time on the local date `dayUtc` (a UTC midnight standing for the date) + `plusDays`. */
function at(dayUtc: number, plusDays: number, time: { h: number; mi: number }, tz: string): number {
  const d = new Date(dayUtc + plusDays * DAY_MS);
  return zonedTimeToUtc({ y: d.getUTCFullYear(), mo: d.getUTCMonth() + 1, d: d.getUTCDate(), h: time.h, mi: time.mi, s: 0 }, tz);
}

/** Open intervals overlapping [fromMs, toMs), merged and clipped to that range, in order. */
export function openIntervals(w: TransferWindow, fromMs: number, toMs: number): OpenInterval[] {
  if (!(toMs > fromMs)) return [];
  const first = wallTimeOf(fromMs, w.timezone);
  const last = wallTimeOf(toMs, w.timezone);
  // A period starting the day before fromMs's local date can still be open (it crosses midnight or lasts 24 hours).
  const startDay = Date.UTC(first.y, first.mo - 1, first.d) - DAY_MS;
  const endDay = Date.UTC(last.y, last.mo - 1, last.d);
  const raw: OpenInterval[] = [];
  for (let day = startDay; day <= endDay; day += DAY_MS) {
    const weekday = BY_UTC_DAY[new Date(day).getUTCDay()]!;
    for (const p of w.periods) {
      if (!p.days.includes(weekday)) continue;
      const from = hm(p.from);
      const start = at(day, 0, from, w.timezone);
      let end: number;
      if (p.to === '24:00') end = at(day, 1, { h: 0, mi: 0 }, w.timezone);
      else {
        const to = hm(p.to);
        const crosses = to.h * 60 + to.mi <= from.h * 60 + from.mi;
        end = at(day, crosses ? 1 : 0, to, w.timezone);
      }
      if (end <= start) continue;
      const s = Math.max(start, fromMs);
      const e = Math.min(end, toMs);
      if (e > s) raw.push({ start: s, end: e });
    }
  }
  raw.sort((a, b) => a.start - b.start || a.end - b.end);
  const out: OpenInterval[] = [];
  for (const iv of raw) {
    const prev = out[out.length - 1];
    if (prev && iv.start <= prev.end) prev.end = Math.max(prev.end, iv.end);
    else out.push({ ...iv });
  }
  return out;
}

/** Whether the window is open at `now`. A null window is always open. */
export function isOpen(w: TransferWindow | null, now: number): boolean {
  if (!w) return true;
  return openIntervals(w, now, now + 1).length > 0;
}

/**
 * When the window is next open, at or after `now`: `now` itself while it is open (and for a null window), otherwise the
 * start of the next opening within 8 days; null when it does not open in that time.
 */
export function nextOpening(w: TransferWindow | null, now: number): number | null {
  if (!w) return now;
  const iv = openIntervals(w, now, now + WINDOW_LOOKAHEAD_MS)[0];
  return iv ? iv.start : null;
}

/**
 * When the window next closes: while open, the end of the current opening; while closed, the end of the next one.
 * Null for a null window, and when the closing is more than 8 days ahead (a window open around the clock).
 */
export function nextClosing(w: TransferWindow | null, now: number): number | null {
  if (!w) return null;
  const horizon = now + WINDOW_LOOKAHEAD_MS;
  const iv = openIntervals(w, now, horizon)[0];
  if (!iv || iv.end >= horizon) return null;
  return iv.end;
}

/** Milliseconds the window is open between two instants (all of them for a null window). */
export function openMsBetween(w: TransferWindow | null, fromMs: number, toMs: number): number {
  if (!(toMs > fromMs)) return 0;
  if (!w) return toMs - fromMs;
  return openIntervals(w, fromMs, toMs).reduce((sum, iv) => sum + (iv.end - iv.start), 0);
}

/**
 * The estimate: when `workMs` of open time, starting at `now`, has passed (the instant the last of it ends). `now`
 * for no work; null when that is more than 400 days ahead.
 */
export function finishAfter(w: TransferWindow | null, now: number, workMs: number): number | null {
  if (!(workMs > 0)) return now;
  if (!w) return now + workMs;
  let left = workMs;
  for (let from = now; from < now + FINISH_HORIZON_MS; from += 7 * DAY_MS) {
    for (const iv of openIntervals(w, from, from + 7 * DAY_MS)) {
      const len = iv.end - iv.start;
      if (len >= left) return iv.start + left;
      left -= len;
    }
  }
  return null;
}

const DAY_LABEL: Record<Weekday, string> = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

function describeDays(days: readonly Weekday[]): string {
  const set = new Set(days);
  if (set.size === 7) return 'Every day';
  const idx = WEEKDAYS.map((d, i) => (set.has(d) ? i : -1)).filter((i) => i >= 0);
  const runs: string[] = [];
  for (let i = 0; i < idx.length; ) {
    let j = i;
    while (j + 1 < idx.length && idx[j + 1] === idx[j]! + 1) j++;
    const a = DAY_LABEL[WEEKDAYS[idx[i]!]!];
    const b = DAY_LABEL[WEEKDAYS[idx[j]!]!];
    runs.push(j === i ? a : j === i + 1 ? `${a}, ${b}` : `${a}–${b}`);
    i = j + 1;
  }
  return runs.join(', ');
}

/** "Mon–Fri 23:00–05:00, Sat, Sun all day (Europe/Helsinki)"; "Any time" for a null window. */
export function describeWindow(w: TransferWindow | null): string {
  if (!w) return 'Any time';
  const parts = w.periods.map((p) => {
    const allDay = p.from === '00:00' && (p.to === '24:00' || p.to === '00:00');
    const hours = allDay ? 'all day' : p.from === p.to ? `${p.from} for 24 hours` : `${p.from}–${p.to}`;
    return `${describeDays(p.days)} ${hours}`;
  });
  return `${parts.join(', ')} (${w.timezone})`;
}
